import React, { useState } from 'react';
import { useData } from '../context/DataContext';
import { Patient, Encounter } from '../types';
import { FlaskConicalIcon, CheckCircleIcon } from 'lucide-react';
export const LaboratoryDashboard: React.FC = () => {
  const {
    patients,
    updateEncounter,
    addNotification
  } = useData();
  const [selectedEncounter, setSelectedEncounter] = useState<{
    patient: Patient;
    encounter: Encounter;
  } | null>(null);
  const [labResults, setLabResults] = useState<Record<string, string>>({});
  const labQueue = patients.flatMap(p => p.encounters.filter(e => {
    if (e.status === 'Lab Ordered') return true;
    // Also show encounters where clinician ordered lab tests but kept file open
    if (e.status === 'With Clinician' || e.status === 'Radiology Ordered') {
      const hasLabOrders = Object.entries(e.investigations.ordered).some(([k, v]) => v === true && k !== 'scanUltrasound' && k !== 'cxr' && k !== 'other') || e.investigations.ordered.other && e.investigations.ordered.other.trim() !== '';
      const hasNoResults = !e.investigations.labResults || Object.keys(e.investigations.labResults).length === 0;
      return hasLabOrders && hasNoResults;
    }
    return false;
  }).map(e => ({
    patient: p,
    encounter: e
  })));
  const handleResultChange = (testKey: string, value: string) => {
    setLabResults(prev => ({
      ...prev,
      [testKey]: value
    }));
  };
  const handleSaveResults = () => {
    if (!selectedEncounter) return;
    const {
      patient,
      encounter
    } = selectedEncounter;
    const updatedEncounter: Encounter = {
      ...encounter,
      investigations: {
        ...encounter.investigations,
        labResults: {
          ...(encounter.investigations.labResults || {}),
          ...labResults
        }
      },
      status: 'With Clinician' // Send back to clinician
    };
    updateEncounter(patient.id, updatedEncounter);
    addNotification({
      targetRole: 'Clinician',
      message: `Lab results ready for ${patient.personalDetails.fullNames}`,
      patientId: patient.id
    });
    setSelectedEncounter(null);
    setLabResults({});
  };
  const openEncounter = (item: {
    patient: Patient;
    encounter: Encounter;
  }) => {
    setSelectedEncounter(item);
    setLabResults(item.encounter.investigations.labResults || {});
  };
  if (selectedEncounter) {
    const {
      patient,
      encounter
    } = selectedEncounter;
    const ordered = encounter.investigations.ordered;
    const renderTestInput = (key: string, label: string, type: 'text' | 'select' = 'text', options: string[] = []) => {
      if (!ordered[key as keyof typeof ordered]) return null;
      return <div key={key} className="mb-4 bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-700">
          <label className="block text-sm font-medium text-teal-800 mb-2" data-id="element-701">
            {label}
          </label>
          {type === 'select' ? <select value={labResults[key] || ''} onChange={e => handleResultChange(key, e.target.value)} className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-702">
              <option value="" data-id="element-703">Select result...</option>
              {options.map(opt => <option key={opt} value={opt} data-id="element-704">
                  {opt}
                </option>)}
            </select> : <input type="text" value={labResults[key] || ''} onChange={e => handleResultChange(key, e.target.value)} placeholder={`Enter ${label} result...`} className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-705" />}
        </div>;
    };
    const renderMultiFieldTest = (key: string, label: string, fields: {
      id: string;
      label: string;
      placeholder: string;
    }[]) => {
      if (!ordered[key as keyof typeof ordered]) return null;
      return <div key={key} className="mb-4 bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-706">
          <h4 className="block text-sm font-bold text-teal-800 mb-3 border-b pb-2" data-id="element-707">
            {label}
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-708">
            {fields.map(field => {
            const resultKey = `${key}_${field.id}`;
            return <div key={resultKey} data-id="element-709">
                  <label className="block text-xs font-medium text-gray-600 mb-1" data-id="element-710">
                    {field.label}
                  </label>
                  <input type="text" value={labResults[resultKey] || ''} onChange={e => handleResultChange(resultKey, e.target.value)} placeholder={field.placeholder} className="w-full border border-gray-300 rounded-md shadow-sm p-2 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-711" />
                </div>;
          })}
          </div>
        </div>;
    };
    return <div className="bg-white shadow rounded-lg border border-gray-100 p-6 max-w-4xl mx-auto" data-id="element-712">
        <div className="flex justify-between items-center mb-6 border-b pb-4 sticky top-0 bg-white z-10" data-id="element-713">
          <div data-id="element-714">
            <h2 className="text-xl font-bold text-gray-800" data-id="element-715">
              Laboratory Results Entry
            </h2>
            <p className="text-sm text-gray-500" data-id="element-716">
              Patient: {patient.personalDetails.fullNames} | File:{' '}
              {patient.personalDetails.fileNo}
            </p>
          </div>
          <button onClick={() => setSelectedEncounter(null)} className="text-gray-500 hover:text-gray-700 font-medium" data-id="element-717">
            Cancel
          </button>
        </div>

        <div className="space-y-6 bg-slate-50 p-4 rounded-lg" data-id="element-718">
          <h3 className="text-lg font-semibold text-gray-700 border-b border-gray-200 pb-2" data-id="element-719">
            Ordered Tests
          </h3>

          {/* HAEMATOLOGY */}
          {renderMultiFieldTest('fbc', 'Full Blood Count (FBC)', [{
          id: 'wbc',
          label: 'WBC',
          placeholder: 'x10^9/L'
        }, {
          id: 'rbc',
          label: 'RBC',
          placeholder: 'x10^12/L'
        }, {
          id: 'hb',
          label: 'Hemoglobin (Hb)',
          placeholder: 'g/dL'
        }, {
          id: 'hct',
          label: 'Hematocrit (HCT)',
          placeholder: '%'
        }, {
          id: 'plt',
          label: 'Platelets',
          placeholder: 'x10^9/L'
        }, {
          id: 'mcv',
          label: 'MCV',
          placeholder: 'fL'
        }])}
          {renderTestInput('bloodGroup', 'Blood Group', 'select', ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'])}
          {renderTestInput('cd4Count', 'CD4 Count')}

          {/* CHEMISTRY */}
          {renderTestInput('urea', 'Urea')}
          {renderTestInput('creatinine', 'Creatinine')}
          {renderTestInput('fbs', 'Fasting Blood Sugar (FBS)')}
          {renderTestInput('rbs', 'Random Blood Sugar (RBS)')}
          {renderTestInput('gtt', 'Glucose Tolerance Test (GTT)')}
          {renderTestInput('postPrandialSugar', '2 Hours Post-Prandial Sugar')}

          {/* SEROLOGY */}
          {renderTestInput('rpr', 'Rapid Plasma Reagin (RPR)', 'select', ['Reactive', 'Non-Reactive'])}
          {renderTestInput('hiv', 'HIV', 'select', ['Reactive', 'Non-Reactive', 'Indeterminate'])}
          {renderTestInput('syphilis', 'Syphilis', 'select', ['Positive', 'Negative'])}
          {renderTestInput('genitalSwab', 'Genital Swab')}
          {renderTestInput('gonorrheaAg', 'Gonorrhea Ag test', 'select', ['Positive', 'Negative'])}

          {/* PARASITOLOGY */}
          {renderMultiFieldTest('urinalysis', 'Urinalysis', [{
          id: 'color',
          label: 'Color',
          placeholder: 'e.g. Yellow'
        }, {
          id: 'clarity',
          label: 'Clarity',
          placeholder: 'e.g. Clear'
        }, {
          id: 'ph',
          label: 'pH',
          placeholder: 'e.g. 6.0'
        }, {
          id: 'protein',
          label: 'Protein',
          placeholder: 'Negative / Trace / +'
        }, {
          id: 'glucose',
          label: 'Glucose',
          placeholder: 'Negative / Trace / +'
        }, {
          id: 'ketones',
          label: 'Ketones',
          placeholder: 'Negative / Trace / +'
        }, {
          id: 'blood',
          label: 'Blood',
          placeholder: 'Negative / Trace / +'
        }, {
          id: 'leukocytes',
          label: 'Leukocytes',
          placeholder: 'Negative / Trace / +'
        }])}
          {renderTestInput('mpsRdt', 'Malaria Parasites', 'select', ['Positive', 'Negative'])}

          {/* HEPATITIS PROFILE */}
          {renderTestInput('hbsAg', 'HBs Ag', 'select', ['Positive', 'Negative'])}
          {renderTestInput('hbcAg', 'HBc Ag', 'select', ['Positive', 'Negative'])}
          {renderTestInput('hbeAg', 'HBe Ag', 'select', ['Positive', 'Negative'])}
          {renderTestInput('hcv', 'HCV', 'select', ['Positive', 'Negative'])}
          {renderTestInput('hav', 'HAV', 'select', ['Positive', 'Negative'])}

          {/* MOLECULAR TESTS */}
          {renderTestInput('viralLoad', 'HIV Viral Load')}

          {/* OTHERS */}
          {ordered.other && <div className="mb-4 bg-white p-4 rounded-md border border-gray-200 shadow-sm" data-id="element-720">
              <label className="block text-sm font-medium text-teal-800 mb-2" data-id="element-721">
                Other Tests: {ordered.other}
              </label>
              <textarea rows={4} value={labResults['other'] || ''} onChange={e => handleResultChange('other', e.target.value)} placeholder="Enter results for other tests..." className="w-full border border-gray-300 rounded-md shadow-sm p-3 text-sm focus:ring-teal-500 focus:border-teal-500" data-id="element-722" />
            </div>}

          <div className="flex justify-end pt-4 border-t border-gray-200" data-id="element-723">
            <button onClick={handleSaveResults} className="inline-flex items-center px-6 py-3 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500" data-id="element-724">
              <CheckCircleIcon className="-ml-1 mr-2 h-5 w-5" data-id="element-725" />
              Save Results & Notify Clinician
            </button>
          </div>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-726">
      <h2 className="text-xl font-bold text-gray-800" data-id="element-727">Laboratory Queue</h2>
      <div className="bg-white shadow overflow-hidden sm:rounded-md border border-gray-100" data-id="element-728">
        <ul className="divide-y divide-gray-200" data-id="element-729">
          {labQueue.map(item => <li key={item.encounter.id} data-id="element-730">
              <div className="px-4 py-4 sm:px-6 hover:bg-gray-50 flex items-center justify-between" data-id="element-731">
                <div className="flex items-center" data-id="element-732">
                  <div className="flex-shrink-0" data-id="element-733">
                    <FlaskConicalIcon className="h-8 w-8 text-orange-500" data-id="element-734" />
                  </div>
                  <div className="ml-4" data-id="element-735">
                    <p className="text-sm font-medium text-teal-600 truncate" data-id="element-736">
                      {item.patient.personalDetails.fullNames}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-737">
                      File No: {item.patient.personalDetails.fileNo}
                    </p>
                  </div>
                </div>
                <button onClick={() => openEncounter(item)} className="inline-flex items-center px-3 py-1 border border-transparent shadow-sm text-sm leading-4 font-medium rounded-md text-white bg-teal-600 hover:bg-teal-700" data-id="element-738">
                  Enter Results
                </button>
              </div>
            </li>)}
          {labQueue.length === 0 && <li className="px-4 py-8 text-center text-gray-500" data-id="element-739">
              No pending lab orders.
            </li>}
        </ul>
      </div>
    </div>;
};